/**
 * Unified Inbox — one row per onboarding client with three quantitative
 * columns (Messages · Tasks · Intake Forms). Each cell has a green→amber→
 * red pill so an internal owner can see at a glance which clients are
 * behind on which axis.
 *
 * Assignee filter surfaces just the rows a specific teammate owns
 * (Dan / Olivia / Lee Ann / <current user's first name> / All). Clicking a
 * row opens the client conversation view at /messages/:clientId (existing
 * MessagesPage) for the full drill-in.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUnifiedInbox, getAgencies } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Loader2, MessageSquare, ListChecks, ChevronRight,
  RefreshCw, Users, Search as SearchIcon, X as XIcon,
} from "lucide-react";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Base filter set. "mine" is a special-cased value: the label is replaced
// at render-time with the current user's first name (e.g. "Daryl") so the
// pill personalises without any backend wiring changes.
const ASSIGNEES = ["All", "Dan", "Olivia", "Lee Ann", "mine"];

// Three-bucket product chips — mirror the Feb 2026 revised task flow doc.
const PRODUCT_CHIPS = [
  { id: "all",            label: "All products" },
  { id: "marketing",      label: "Marketing" },
  { id: "web_design",     label: "Website" },
  { id: "prospect_tools", label: "ProspectMax" },
  { id: "seo_aeo",        label: "SEO/AEO" },
];

const LIFECYCLE_CHIPS = [
  { id: "all",            label: "All" },
  { id: "onboarding",     label: "Onboarding" },
  { id: "monthly_active", label: "Monthly Active" },
];

// Traffic-light styles keyed by the backend's light values.
const LIGHT_STYLE = {
  red:   "bg-red-100 text-red-800 border-red-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  green: "bg-emerald-50 text-emerald-800 border-emerald-200",
  grey:  "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function Pill({ count, light, icon: Icon, label, onClick }) {
  const style = LIGHT_STYLE[light] || LIGHT_STYLE.grey;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold hover:brightness-95 hover:shadow-sm transition ${style}`}
      title={`${count} ${label} · status: ${light} — click to open`}
      data-testid={`inbox-pill-${label.toLowerCase()}-${light}`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span>{count}</span>
    </button>
  );
}

export default function UnifiedInboxPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ clients: 0, messages: 0, tasks: 0 });
  const [assignee, setAssignee] = useState("All");
  const [productFilter, setProductFilter] = useState("all");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [replyNeededOnly, setReplyNeededOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [agencies, setAgencies] = useState([]);
  // Global message-body search (separate from the row-level client/agency filter).
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalHits, setGlobalHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (assignee && assignee !== "All") params.assignee = assignee.toLowerCase();
      if (productFilter && productFilter !== "all") params.product = productFilter;
      if (lifecycleFilter && lifecycleFilter !== "all") params.lifecycle = lifecycleFilter;
      if (agencyFilter && agencyFilter !== "all") params.agency_id = agencyFilter;
      if (search.trim()) params.search = search.trim();
      if (replyNeededOnly) params.reply_needed_only = true;
      const { data } = await axios.get(`${API}/inbox`, { params });
      setRows(data.rows || []);
      setTotals(data.totals || { clients: 0, messages: 0, tasks: 0 });
    } catch (e) {
      console.error("Failed to load inbox:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ },
    [assignee, productFilter, lifecycleFilter, agencyFilter, replyNeededOnly, search]);

  // Rep-facing users don't have staff filters — they always see just their clients
  const showFilters = ["super_admin", "manager", "ops_coordinator"].includes(user?.role);

  // Display label for the "mine" filter — swap in the current user's first
  // name so the pill personalises (e.g. "Daryl" for Daryl's account).
  const myFirstName = useMemo(() => {
    const nm = (user?.name || user?.email || "").trim();
    if (!nm) return "Me";
    return nm.split(/[\s@]/)[0] || "Me";
  }, [user]);
  const labelFor = (name) => (name === "mine" ? myFirstName : name);

  // Dedupe: if the logged-in user's first name matches one of the static chips
  // (e.g. Olivia signed in as Olivia), drop the static duplicate so we don't
  // render "Olivia" twice on the row. The "mine" chip covers her personally.
  const visibleAssignees = useMemo(() => {
    const myLower = myFirstName.toLowerCase();
    return ASSIGNEES.filter((name) => name === "mine" || name.toLowerCase() !== myLower);
  }, [myFirstName]);

  const activeChipClass = (name) =>
    assignee === name
      ? "bg-primary text-primary-foreground border-primary"
      : "bg-white text-foreground border-zinc-200 hover:border-primary/40";

  // Internal staff can filter by agency. Loaded lazily so reps don't pay for it.
  useEffect(() => {
    if (!showFilters) return;
    getAgencies().then((r) => setAgencies(r.data || [])).catch(() => {});
  }, [showFilters]);

  // Debounced global message search — fires 350ms after you stop typing.
  useEffect(() => {
    const q = globalSearch.trim();
    if (q.length < 2) { setGlobalHits([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/messages/search`, { params: { q } });
        setGlobalHits(data.hits || []);
      } catch (_) { setGlobalHits([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [globalSearch]);

  // Chip helper — DRY the active vs. inactive styling for the new filter rows.
  const chipClass = (active) =>
    active
      ? "bg-primary text-primary-foreground border-primary"
      : "bg-white text-foreground border-zinc-200 hover:border-primary/40";

  return (
    <div className="p-6 space-y-6" data-testid="unified-inbox-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-outfit font-semibold">Unified Inbox</h1>
          <p className="text-sm text-muted-foreground">
            One row per active client — messages and tasks in one view.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchData}
          data-testid="inbox-refresh"
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryTile icon={Users} label="Clients" value={totals.clients} tint="text-primary" />
        <SummaryTile icon={MessageSquare} label="Messages" value={totals.messages} tint="text-orange-600" />
        <SummaryTile icon={ListChecks} label="Tasks" value={totals.tasks} tint="text-emerald-600" />
      </div>

      {/* Global message search — searches comment bodies across every
          thread the viewer can see. Separate from the row filter below. */}
      <div className="relative" data-testid="inbox-global-search">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          placeholder='Search messages — "ad proof", "disney contract", "domain access"…'
          className="pl-9 pr-9"
          data-testid="inbox-global-search-input"
        />
        {globalSearch && (
          <button
            type="button"
            onClick={() => { setGlobalSearch(""); setGlobalHits([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive"
            aria-label="Clear search"
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
        {globalSearch.trim().length >= 2 && (
          <div className="mt-2 rounded-md border bg-white shadow-sm overflow-hidden" data-testid="inbox-search-results">
            {searching ? (
              <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Searching…
              </div>
            ) : globalHits.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">No matches.</div>
            ) : (
              <ul className="max-h-72 overflow-auto divide-y">
                {globalHits.map((h) => (
                  <li key={`${h.thread_id}-${h.comment_id}`}>
                    <button
                      type="button"
                      onClick={() => navigate(`/messages/${h.client_id}`)}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex flex-col gap-0.5"
                      data-testid={`inbox-search-hit-${h.comment_id}`}
                    >
                      <div className="text-xs font-semibold text-primary truncate">{h.client_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        <span className="font-medium text-foreground">{h.author_name || "—"}:</span>{" "}
                        {h.snippet}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Primary filter row — Product · Reply-needed · Client search.
          Rep-friendly: always visible, three cheap toggles, no dropdowns. */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="inbox-product-filter">
        <span className="text-xs font-semibold text-muted-foreground mr-1">Product:</span>
        {PRODUCT_CHIPS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setProductFilter(p.id)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${chipClass(productFilter === p.id)}`}
            data-testid={`inbox-product-${p.id}`}
          >
            {p.label}
          </button>
        ))}
        <span className="ml-4" />
        <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer select-none" data-testid="inbox-reply-needed-toggle">
          <input
            type="checkbox"
            checked={replyNeededOnly}
            onChange={(e) => setReplyNeededOnly(e.target.checked)}
            className="rounded"
          />
          Reply needed only
        </label>
        <div className="relative ml-auto">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={showFilters ? "Filter by client or agency…" : "Filter by client…"}
            className="pl-8 h-8 w-56 text-xs"
            data-testid="inbox-client-search"
          />
        </div>
      </div>

      {/* Secondary filter row — Lifecycle (both audiences) · Agency (internal). */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-muted-foreground mr-1">Stage:</span>
        {LIFECYCLE_CHIPS.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => setLifecycleFilter(l.id)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${chipClass(lifecycleFilter === l.id)}`}
            data-testid={`inbox-lifecycle-${l.id}`}
          >
            {l.label}
          </button>
        ))}
        {showFilters && agencies.length > 1 && (
          <>
            <span className="ml-4 text-xs font-semibold text-muted-foreground">Agency:</span>
            <select
              value={agencyFilter}
              onChange={(e) => setAgencyFilter(e.target.value)}
              className="text-xs font-semibold px-2 py-1.5 rounded border border-zinc-200 bg-white"
              data-testid="inbox-agency-filter"
            >
              <option value="all">All agencies</option>
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Assignee filter */}
      {showFilters && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="inbox-assignee-filter">
          <span className="text-xs font-semibold text-muted-foreground mr-1">Filter by staff:</span>
          {visibleAssignees.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setAssignee(name)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${activeChipClass(name)}`}
              data-testid={`inbox-filter-${name.toLowerCase().replace(" ", "-")}`}
            >
              {labelFor(name)}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="overflow-hidden" data-testid="inbox-table">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_auto_auto_auto] gap-x-3 gap-y-0 items-center px-4 py-2.5 border-b bg-zinc-50 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
          <div>Client</div>
          <div>Partner</div>
          <div>Sales Rep</div>
          <div>Owner(s)</div>
          <div className="text-center">Messages</div>
          <div className="text-center">Tasks</div>
          <div />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground" data-testid="inbox-empty">
            {assignee === "All"
              ? "🎉 Inbox zero — nothing pending on any active client."
              : `Nothing pending for ${labelFor(assignee)}. Nice!`}
          </div>
        ) : (
          rows.map((r) => {
            // Two click targets: Messages pill → conversation view;
            // Tasks pill / row / arrow → order detail so users land
            // directly where the tasks live.
            const openMessages = () => {
              const suffix = assignee && assignee.toLowerCase() !== "all"
                ? `?staff=${encodeURIComponent(assignee)}`
                : "";
              navigate(`/messages/${r.client_id}${suffix}`);
            };
            const openOrder = () => {
              if (r.primary_project_id) {
                navigate(`/orders/${r.primary_project_id}`);
              } else {
                openMessages();
              }
            };
            return (
              <div
                key={r.client_id}
                role="button"
                tabIndex={0}
                onClick={openOrder}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openOrder(); } }}
                data-testid={`inbox-row-${r.client_id}`}
                className="w-full text-left grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_auto_auto_auto] gap-x-3 items-center px-4 py-3 border-b hover:bg-primary/5 focus-visible:bg-primary/10 outline-none cursor-pointer transition group"
              >
              <div className="min-w-0">
                <div className="font-semibold truncate">{r.client_name || "Unknown"}</div>
                {r.worst_light === "red" && (
                  <div className="text-[10px] font-semibold text-red-600 mt-0.5">Needs attention</div>
                )}
              </div>
              <div className="min-w-0 text-sm text-muted-foreground truncate">
                {r.agency_name || "—"}
              </div>
              <div className="min-w-0 text-sm text-muted-foreground truncate">
                {r.rep_name || "—"}
              </div>
              <div className="min-w-0 text-xs text-muted-foreground truncate">
                {r.task_assignees?.length ? r.task_assignees.join(", ") : "—"}
              </div>
              <div className="justify-self-center">
                <Pill count={r.messages.count} light={r.messages.light} icon={MessageSquare} label="Messages" onClick={openMessages} />
              </div>
              <div className="justify-self-center">
                <Pill count={r.tasks.count} light={r.tasks.light} icon={ListChecks} label="Tasks" onClick={openOrder} />
              </div>
              <div className="justify-self-end">
                <ChevronRight className="h-4 w-4 text-zinc-300 group-hover:text-primary transition" />
              </div>
              </div>
            );
          })
        )}
      </Card>

      <p className="text-[11px] text-muted-foreground">
        Click a row (or the Tasks pill / arrow) to jump to the order detail. Click the Messages pill to open the conversation instead.
        Thresholds — Messages: green &lt;12h · amber 12–24h · red &gt;24h since last external reply.
        Tasks: no due date → grey · due today/tomorrow → amber · past due → red.
      </p>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-lg border bg-white px-4 py-3 flex items-center gap-3">
      <div className={`p-2 rounded-md bg-zinc-100 ${tint}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold">{value}</div>
      </div>
    </div>
  );
}
